import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  VStack,
  Badge,
} from "@chakra-ui/react";
import {
  FaChartLine,
  FaHeartbeat,
  FaHospital,
  FaRegChartBar,
  FaUserMd,
} from "react-icons/fa";
import {
  IoMdArrowForward,
  IoMdAnalytics,
  IoMdPulse,
  IoMdTrendingUp,
} from "react-icons/io";
import {
  MdInsights,
  MdOutlineHealthAndSafety,
  MdTimeline,
} from "react-icons/md";

import Footer from "../Common/Footer";
import Header from "../Common/Header";
import IconBox from "../pure/Icons/IconBox";
import Theme from "../../constants/Theme";
import { handleCTA } from "../../utils";

// Sample chart data for visualization
const DASHBOARD_METRICS = [
  {
    title: "Patient Outcomes",
    stat: "+28%",
    trend: "Improved YoY",
    icon: IoMdPulse,
    color: "teal.500",
    bgColor: "teal.50",
  },
  {
    title: "Cost Reduction",
    stat: "32%",
    trend: "Lower than average",
    icon: FaRegChartBar,
    color: "blue.500",
    bgColor: "blue.50",
  },
  {
    title: "Readmission Rate",
    stat: "-18%",
    trend: "Decreased since Q1",
    icon: FaHospital,
    color: "purple.500",
    bgColor: "purple.50",
  },
  {
    title: "Treatment Efficacy",
    stat: "94%",
    trend: "Success rate",
    icon: FaHeartbeat,
    color: "red.500",
    bgColor: "red.50",
  },
];

const INSIGHT_TABS = [
  {
    title: "Predictive Analytics",
    content:
      "Harness the power of machine learning to predict patient outcomes and identify at-risk populations before critical events occur. Our algorithms analyze thousands of variables across patient populations to deliver actionable insights.",
    image: "/assets/images/predictive-analytics-healthcare.svg",
    features: [
      "Early risk detection",
      "Outcome prediction",
      "Resource optimization",
    ],
  },
  {
    title: "Population Health",
    content:
      "Gain a comprehensive view of your patient population with stratified insights across demographics, conditions, and social determinants of health. Identify trends and disparities to guide targeted interventions.",
    image: "/assets/images/population-health.gif",
    features: [
      "Demographic analysis",
      "Care gap identification",
      "Intervention tracking",
    ],
  },
  {
    title: "Clinical Decision Support",
    content:
      "Enhance clinical decision-making with contextual AI recommendations based on the latest evidence and best practices. Our system integrates seamlessly with your workflow to provide guidance when and where you need it.",
    image: "/assets/images/clinical-decision.gif",
    features: [
      "Evidence-based recommendations",
      "Contextual alerts",
      "Treatment path optimization",
    ],
  },
];

const DASHBOARD_FEATURES = [
  {
    title: "Real-time Analytics",
    description:
      "Monitor vital metrics and KPIs in real-time with dynamic dashboards that update as new data streams in.",
    icon: IoMdAnalytics,
  },
  {
    title: "Predictive Insights",
    description:
      "Leverage advanced machine learning to forecast patient outcomes and identify intervention opportunities.",
    icon: MdInsights,
  },
  {
    title: "Clinical Workflow Integration",
    description:
      "Seamlessly incorporate analytics into your existing clinical workflows for effortless adoption.",
    icon: MdTimeline,
  },
  {
    title: "Patient-centric Views",
    description:
      "Drill down from population trends to individual patient journeys with context-aware visualizations.",
    icon: FaUserMd,
  },
];

const HealthcareAnalytics = () => {
  const bgGradient = "linear(to-r, teal.600, blue.600)";
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const patternBg = useColorModeValue(
    "url('/assets/patterns/pattern-dots.svg')",
    "url('/assets/patterns/dark-pattern-dots.svg')"
  );

  return (
    <Box>
      <Header minimal={false} />

      {/* Hero Section */}
      <Box
        as="section"
        bgGradient={bgGradient}
        position="relative"
        overflow="hidden"
        py={{ base: 20, md: 28 }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.xl" position="relative">
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={{ base: 10, lg: 20 }}
            align="center"
          >
            <VStack
              spacing={6}
              align={{ base: "center", lg: "flex-start" }}
              textAlign={{ base: "center", lg: "left" }}
              maxW={{ base: "full", lg: "50%" }}
            >
              <Badge
                px={4}
                py={1}
                bg="rgba(255,255,255,0.15)"
                color="white"
                fontSize="md"
                borderRadius="full"
                fontWeight="semibold"
              >
                Healthcare Analytics Platform
              </Badge>
              <Heading
                size="2xl"
                lineHeight="1.2"
                fontWeight="bold"
                color="white"
              >
                Transform Healthcare Data Into
                <Text
                  as="span"
                  fontWeight="extrabold"
                  bgGradient="linear(to-r, cyan.300, purple.300)"
                  bgClip="text"
                >
                  {" "}
                  Actionable Insights
                </Text>
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.900">
                Our AI-powered analytics platform helps healthcare leaders
                identify trends, predict outcomes, and improve patient care
                through advanced data visualization and machine learning.
              </Text>
              <HStack spacing={4} pt={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="teal.600"
                  _hover={{ bg: "white", transform: "translateY(-2px)" }}
                  borderRadius="full"
                  px={8}
                  fontWeight="bold"
                  boxShadow="lg"
                  rightIcon={<IoMdArrowForward />}
                  onClick={() => handleCTA("demo")}
                  transition="all 0.3s"
                >
                  Request Demo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.400"
                  borderRadius="full"
                  px={8}
                  color="white"
                  _hover={{ bg: "whiteAlpha.200" }}
                  fontWeight="medium"
                  onClick={() => {
                    const insightsSection = document.getElementById("insights");
                    insightsSection?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  Explore Insights
                </Button>
              </HStack>
            </VStack>
            <Box
              maxW={{ base: "100%", lg: "50%" }}
              position="relative"
              transform={{ base: "scale(1)", lg: "scale(1.1)" }}
            >
              <Box
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                bg={cardBg}
                p={4}
                backdropFilter="blur(10px)"
                border="1px solid"
                borderColor="whiteAlpha.200"
                transform={{
                  base: "none",
                  lg: "perspective(1000px) rotateY(-5deg)",
                }}
                transition="all 0.3s"
                _hover={{ transform: "perspective(1000px) rotateY(0deg)" }}
              >
                <Image
                  src="/assets/images/healthcare-analytics-dashboard.svg"
                  alt="Healthcare Analytics Dashboard"
                  fallbackSrc="https://via.placeholder.com/600x400?text=Healthcare+Analytics+Dashboard"
                  borderRadius="lg"
                  height={300}
                />
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Key Metrics Dashboard Section */}
      <Box as="section" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="teal.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                Data-Driven Healthcare
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Real-Time Healthcare Intelligence
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Monitor key metrics that matter to your healthcare organization
                with our interactive dashboards and real-time analytics.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              spacing={8}
              w="full"
            >
              {DASHBOARD_METRICS.map((metric, idx) => (
                <Box
                  key={idx}
                  bg={cardBg}
                  borderRadius="xl"
                  boxShadow="md"
                  p={6}
                  position="relative"
                  overflow="hidden"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
                >
                  <Flex justify="space-between" align="center" mb={4}>
                    <Stat>
                      <StatLabel fontSize="md" fontWeight="medium">
                        {metric.title}
                      </StatLabel>
                      <StatNumber
                        fontSize="3xl"
                        fontWeight="bold"
                        color={metric.color}
                      >
                        {metric.stat}
                      </StatNumber>
                      <StatHelpText>
                        <Flex align="center">
                          <Icon as={IoMdTrendingUp} mr={1} />
                          {metric.trend}
                        </Flex>
                      </StatHelpText>
                    </Stat>
                    <Flex
                      borderRadius="full"
                      w="50px"
                      h="50px"
                      bg={metric.bgColor}
                      justify="center"
                      align="center"
                    >
                      <Icon
                        as={metric.icon}
                        color={metric.color}
                        boxSize="24px"
                      />
                    </Flex>
                  </Flex>

                  <Box
                    h="70px"
                    bg={useColorModeValue("gray.50", "gray.700")}
                    borderRadius="md"
                    position="relative"
                    overflow="hidden"
                  >
                    {/* Simulated chart - in a real app, you'd use a chart library here */}
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      height="100%"
                      bgGradient={`linear(to-t, ${metric.color}, transparent)`}
                      opacity="0.2"
                      clipPath="polygon(0 50%, 10% 45%, 20% 60%, 30% 40%, 40% 55%, 50% 30%, 60% 40%, 70% 20%, 80% 35%, 90% 25%, 100% 50%, 100% 100%, 0 100%)"
                    />
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      height="2px"
                      bg={metric.color}
                      opacity="0.7"
                    />
                  </Box>
                </Box>
              ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {/* Main Analytics Dashboard Preview */}
              <Box
                bg={cardBg}
                borderRadius="xl"
                boxShadow="lg"
                overflow="hidden"
                gridRow={{ md: "span 2" }}
                position="relative"
                transition="transform 0.3s"
                _hover={{ transform: "scale(1.02)" }}
              >
                <Box position="relative">
                  <Image
                    src="/assets/images/detailed-analytics.png"
                    alt="Detailed Analytics Dashboard"
                    fallbackSrc="https://via.placeholder.com/800x600?text=Detailed+Analytics+Dashboard"
                    w="full"
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="blackAlpha.300"
                    backdropFilter="blur(3px)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    opacity="0"
                    transition="opacity 0.3s"
                    _hover={{ opacity: 1 }}
                  >
                    <Button
                      colorScheme="teal"
                      size="lg"
                      borderRadius="full"
                      rightIcon={<IoMdArrowForward />}
                      onClick={() => handleCTA("explore_analytics")}
                    >
                      Explore Dashboard
                    </Button>
                  </Box>
                </Box>
                <Box p={6}>
                  <Heading size="md" mb={2}>
                    Enterprise Healthcare Dashboard
                  </Heading>
                  <Text color={useColorModeValue("gray.600", "gray.400")}>
                    Comprehensive views of your organization's performance with
                    drill-down capabilities for detailed analysis.
                  </Text>
                </Box>
              </Box>

              {/* Features */}
              <Box bg={cardBg} borderRadius="xl" boxShadow="md" p={6}>
                <Heading size="md" mb={4}>
                  Powerful Analytics Features
                </Heading>
                <VStack spacing={4} align="stretch">
                  {DASHBOARD_FEATURES.slice(0, 2).map((feature, idx) => (
                    <HStack key={idx} spacing={4}>
                      <Flex
                        minW="12"
                        h="12"
                        borderRadius="md"
                        bg={Theme.STYLES.CUCUMBER_WATER}
                        color="teal.500"
                        justify="center"
                        align="center"
                      >
                        <Icon as={feature.icon} boxSize={6} />
                      </Flex>
                      <Box>
                        <Text fontWeight="semibold">{feature.title}</Text>
                        <Text
                          fontSize="sm"
                          color={useColorModeValue("gray.600", "gray.400")}
                        >
                          {feature.description}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Box bg={cardBg} borderRadius="xl" boxShadow="md" p={6}>
                <Heading size="md" mb={4}>
                  Enterprise Integration
                </Heading>
                <VStack spacing={4} align="stretch">
                  {DASHBOARD_FEATURES.slice(2, 4).map((feature, idx) => (
                    <HStack key={idx} spacing={4}>
                      <Flex
                        minW="12"
                        h="12"
                        borderRadius="md"
                        bg="blue.50"
                        color="blue.500"
                        justify="center"
                        align="center"
                      >
                        <Icon as={feature.icon} boxSize={6} />
                      </Flex>
                      <Box>
                        <Text fontWeight="semibold">{feature.title}</Text>
                        <Text
                          fontSize="sm"
                          color={useColorModeValue("gray.600", "gray.400")}
                        >
                          {feature.description}
                        </Text>
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* AI Insights Section */}
      <Box as="section" py={16} bg={sectionBg} id="insights">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Badge
                colorScheme="purple"
                px={3}
                py={1}
                mb={4}
                borderRadius="full"
                fontSize="sm"
              >
                AI-Powered Insights
              </Badge>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Unlock the Power of Your Healthcare Data
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Transform raw healthcare data into actionable insights with
                advanced AI that identifies patterns, predicts outcomes, and
                recommends interventions.
              </Text>
            </Box>

            <Tabs variant="soft-rounded" colorScheme="teal" size="lg" isLazy>
              <TabList
                justifyContent="center"
                flexWrap="wrap"
                mb={10}
                pb={2}
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                {INSIGHT_TABS.map((tab, idx) => (
                  <Tab
                    key={idx}
                    mx={4}
                    _selected={{
                      color: "white",
                      bg: "teal.500",
                      boxShadow: "md",
                    }}
                    fontWeight="medium"
                  >
                    {tab.title}
                  </Tab>
                ))}
              </TabList>

              <TabPanels>
                {INSIGHT_TABS.map((tab, idx) => (
                  <TabPanel key={idx} px={0}>
                    <Box
                      bg={cardBg}
                      borderRadius="xl"
                      boxShadow="lg"
                      overflow="hidden"
                    >
                      <Stack
                        direction={{ base: "column", lg: "row" }}
                        spacing={0}
                      >
                        <Box
                          flex="1"
                          p={8}
                          borderRight={{ base: "none", lg: "1px solid" }}
                          borderBottom={{ base: "1px solid", lg: "none" }}
                          borderColor={borderColor}
                        >
                          <Heading size="lg" mb={4}>
                            {tab.title}
                          </Heading>
                          <Text
                            fontSize="lg"
                            mb={6}
                            color={useColorModeValue("gray.600", "gray.400")}
                          >
                            {tab.content}
                          </Text>

                          <Grid
                            templateColumns="repeat(auto-fit, minmax(180px, 1fr))"
                            gap={4}
                            mb={6}
                          >
                            {tab.features.map((feature, featureIdx) => (
                              <HStack
                                key={featureIdx}
                                bg={useColorModeValue("gray.50", "gray.700")}
                                p={3}
                                borderRadius="md"
                              >
                                <Icon
                                  as={MdOutlineHealthAndSafety}
                                  color="teal.500"
                                />
                                <Text fontWeight="medium" fontSize="sm">
                                  {feature}
                                </Text>
                              </HStack>
                            ))}
                          </Grid>

                          <Button
                            mt={4}
                            colorScheme="teal"
                            size="lg"
                            rightIcon={<IoMdArrowForward />}
                            onClick={() => handleCTA("learn_more")}
                          >
                            Learn More
                          </Button>
                        </Box>

                        <Box
                          flex="1"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bg={useColorModeValue("gray.50", "gray.700")}
                          p={6}
                        >
                          <Image
                            src={tab.image}
                            alt={`${tab.title} visualization`}
                            fallbackSrc={`https://via.placeholder.com/600x400?text=${tab.title}`}
                            borderRadius="lg"
                            boxShadow="md"
                            maxH="400px"
                          />
                        </Box>
                      </Stack>
                    </Box>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box as="section" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="teal.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                Healthcare Transformation
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Benefits of Advanced Healthcare Analytics
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                See how our platform drives measurable improvements across
                healthcare organizations.
              </Text>
            </Box>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={10}
              w="full"
            >
              <Box
                bg={cardBg}
                borderRadius="xl"
                boxShadow="md"
                p={8}
                borderTop="4px solid"
                borderColor="teal.500"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
              >
                <IconBox
                  h="60px"
                  w="60px"
                  bg={Theme.STYLES.CUCUMBER_WATER}
                  mb={4}
                  borderRadius="lg"
                  color="teal.500"
                >
                  <Icon as={FaChartLine} w={7} h={7} />
                </IconBox>
                <Heading
                  size="md"
                  mb={4}
                  color={useColorModeValue("gray.700", "white")}
                >
                  Improved Patient Outcomes
                </Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>
                  Healthcare organizations using our platform report a 23%
                  average improvement in patient outcomes through early
                  intervention and personalized care paths.
                </Text>
              </Box>

              <Box
                bg={cardBg}
                borderRadius="xl"
                boxShadow="md"
                p={8}
                borderTop="4px solid"
                borderColor="blue.500"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
              >
                <IconBox
                  h="60px"
                  w="60px"
                  bg="blue.50"
                  mb={4}
                  borderRadius="lg"
                  color="blue.500"
                >
                  <Icon as={FaRegChartBar} w={7} h={7} />
                </IconBox>
                <Heading
                  size="md"
                  mb={4}
                  color={useColorModeValue("gray.700", "white")}
                >
                  Operational Efficiency
                </Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>
                  Reduce administrative overhead by up to 35% with automated
                  insights that optimize resource allocation, staffing, and
                  supply chain management.
                </Text>
              </Box>

              <Box
                bg={cardBg}
                borderRadius="xl"
                boxShadow="md"
                p={8}
                borderTop="4px solid"
                borderColor="purple.500"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
              >
                <IconBox
                  h="60px"
                  w="60px"
                  bg="purple.50"
                  mb={4}
                  borderRadius="lg"
                  color="purple.500"
                >
                  <Icon as={FaUserMd} w={7} h={7} />
                </IconBox>
                <Heading
                  size="md"
                  mb={4}
                  color={useColorModeValue("gray.700", "white")}
                >
                  Enhanced Clinical Decision-Making
                </Heading>
                <Text color={useColorModeValue("gray.600", "gray.400")}>
                  Clinicians report 87% more confidence in their decisions when
                  supported by our AI-powered analytics, leading to faster and
                  more accurate diagnoses.
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        as="section"
        py={16}
        bgGradient={bgGradient}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing={8}
            align="center"
            justify="space-between"
            bg="whiteAlpha.100"
            p={8}
            borderRadius="xl"
            backdropFilter="blur(8px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <VStack
              align={{ base: "center", md: "flex-start" }}
              spacing={4}
              textAlign={{ base: "center", md: "left" }}
            >
              <Heading color="white" size="lg">
                Ready to Transform Your Healthcare Analytics?
              </Heading>
              <Text color="whiteAlpha.900">
                Get started with a personalized demo tailored to your
                organization's needs.
              </Text>
            </VStack>
            <HStack spacing={4}>
              <Button
                size="lg"
                bg="white"
                color="teal.600"
                _hover={{ bg: "white", transform: "translateY(-2px)" }}
                borderRadius="full"
                px={8}
                fontWeight="bold"
                rightIcon={<IoMdArrowForward />}
                onClick={() => handleCTA("demo")}
              >
                Request Demo
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                borderRadius="full"
                px={8}
                fontWeight="medium"
                onClick={() => handleCTA("signup")}
              >
                Start Free Trial
              </Button>
            </HStack>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default HealthcareAnalytics;
